import { addDays, startOfDay, toLocalDateString } from './format'
import type { DayCapacity, LocalDateString } from '../types/PublicBookingChannelTypes'

/** Monday-first, as every UK calendar is. The old grid started on Sunday. */
export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

/** Spoken column headers: "Mon" read aloud is "mon", not a day. */
export const WEEKDAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const

export interface CalendarCell {
  date: Date
  key: LocalDateString
  /** False for leading/trailing days borrowed from the adjacent month. */
  inMonth: boolean
  /** Outside the channel's bookable window. */
  outOfRange: boolean
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

export function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1)
}

/** Monday = 0 … Sunday = 6. */
function mondayFirstIndex(date: Date): number {
  return (date.getDay() + 6) % 7
}

/**
 * Builds a fixed 6-row grid for the month containing `cursor`.
 *
 * A constant row count stops the panel resizing as the customer pages
 * through months, which otherwise makes the action bar jump under the cursor.
 */
export function buildMonthGrid(cursor: Date, minDate: Date, maxDate: Date): CalendarCell[] {
  const first = startOfMonth(cursor)
  const leading = mondayFirstIndex(first)
  const gridStart = new Date(first.getFullYear(), first.getMonth(), 1 - leading)

  const min = startOfDay(minDate).getTime()
  const max = startOfDay(maxDate).getTime()

  const cells: CalendarCell[] = []
  for (let index = 0; index < 42; index++) {
    const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + index)
    const time = startOfDay(date).getTime()

    cells.push({
      date,
      key: toLocalDateString(date),
      inMonth: date.getMonth() === first.getMonth(),
      outOfRange: time < min || time > max,
    })
  }

  return cells
}

/** True when paging back from `cursor` would leave the bookable window entirely. */
export function canGoToPreviousMonth(cursor: Date, minDate: Date): boolean {
  const previous = addMonths(startOfMonth(cursor), -1)
  const lastOfPrevious = new Date(previous.getFullYear(), previous.getMonth() + 1, 0)
  return startOfDay(lastOfPrevious).getTime() >= startOfDay(minDate).getTime()
}

export function canGoToNextMonth(cursor: Date, maxDate: Date): boolean {
  const next = addMonths(startOfMonth(cursor), 1)
  return startOfDay(next).getTime() <= startOfDay(maxDate).getTime()
}

/**
 * Derives a day's capacity band from its slot count.
 *
 * Used as a client-side fallback for days the month endpoint has not
 * described — never to overrule what the server said.
 */
export function capacityFromSlotCount(slotCount: number): DayCapacity {
  if (slotCount <= 0) return 'full'
  if (slotCount <= 3) return 'limited'
  return 'open'
}

export function isBookable(capacity: DayCapacity | undefined): boolean {
  return capacity !== 'full' && capacity !== 'closed'
}

/**
 * Splits the grid into weeks, one ARIA row each. Every week is kept, even one
 * with no day of this month, so the six-row height still never changes.
 */
export function toWeeks(cells: CalendarCell[]): CalendarCell[][] {
  const weeks: CalendarCell[][] = []
  for (let index = 0; index < cells.length; index += 7) {
    weeks.push(cells.slice(index, index + 7))
  }
  return weeks
}

/** Reads a LocalDateString back as a local Date at midnight. */
export function fromLocalDateString(value: string): Date {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/** The keys the calendar grid answers, as in the WAI-ARIA date picker pattern. */
export type CalendarNavigationKey =
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'ArrowUp'
  | 'ArrowDown'
  | 'Home'
  | 'End'
  | 'PageUp'
  | 'PageDown'

const NAVIGATION_KEYS: ReadonlySet<string> = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'PageUp',
  'PageDown',
])

export function isCalendarNavigationKey(key: string): key is CalendarNavigationKey {
  return NAVIGATION_KEYS.has(key)
}

function sameMonthDay(from: Date, months: number): Date {
  const first = addMonths(from, months)
  const lastDay = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
  return new Date(first.getFullYear(), first.getMonth(), Math.min(from.getDate(), lastDay))
}

/**
 * Where a key press moves the calendar's focus, or null to stay put.
 *
 * Days the customer cannot book are stepped over: Right from a Saturday lands
 * on Monday when the garage is shut on Sundays, and Down keeps going a week at
 * a time until it finds an open day. The walk stops at the bookable window, so
 * a key that has nowhere to go leaves focus where it was rather than parking
 * it on a closed day.
 *
 * Home and End are the first and last open days of the focused week; Page Up
 * and Page Down are the same date a month away, or the nearest open day in that
 * month when that date is shut.
 */
export function findKeyboardTarget(
  from: Date,
  key: CalendarNavigationKey,
  isOpen: (date: Date) => boolean,
  minDate: Date,
  maxDate: Date
): Date | null {
  const min = startOfDay(minDate).getTime()
  const max = startOfDay(maxDate).getTime()
  const inRange = (date: Date) => {
    const time = startOfDay(date).getTime()
    return time >= min && time <= max
  }
  const bookable = (date: Date) => inRange(date) && isOpen(date)

  const walk = (start: Date, step: number): Date | null => {
    const beyond = (date: Date) =>
      step > 0 ? startOfDay(date).getTime() > max : startOfDay(date).getTime() < min
    for (let candidate = addDays(start, step); !beyond(candidate); candidate = addDays(candidate, step)) {
      if (bookable(candidate)) return candidate
    }
    return null
  }

  switch (key) {
    case 'ArrowRight':
      return walk(from, 1)
    case 'ArrowLeft':
      return walk(from, -1)
    case 'ArrowDown':
      return walk(from, 7)
    case 'ArrowUp':
      return walk(from, -7)
    case 'Home':
    case 'End': {
      const monday = addDays(from, -((from.getDay() + 6) % 7))
      const days = Array.from({ length: 7 }, (_, index) => addDays(monday, index))
      const ordered = key === 'Home' ? days : days.reverse()
      return ordered.find(bookable) ?? null
    }
    case 'PageUp':
    case 'PageDown': {
      const target = sameMonthDay(from, key === 'PageDown' ? 1 : -1)
      if (bookable(target)) return target
      const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
      for (let offset = 1; offset < lastDay; offset++) {
        for (const day of [target.getDate() + offset, target.getDate() - offset]) {
          if (day < 1 || day > lastDay) continue
          const candidate = new Date(target.getFullYear(), target.getMonth(), day)
          if (bookable(candidate)) return candidate
        }
      }
      return null
    }
  }
}
